<script setup lang="ts">
import { ref, onMounted } from 'vue'
import AppModal from '@/components/ui/AppModal.vue'
import { useContactConfigStore } from '@/domains/home/services/nir/configurations/stores/contact_config.ts'
import { useNotificationsStore } from '@/app/notify/notifications.ts'
import type { ContactEntry } from '@/domains/home/services/nir/configurations/types/contacts.ts'

const store = useContactConfigStore()
const notify = useNotificationsStore()

const loading = ref(false)
const showForm = ref(false)
const saving = ref(false)
const editingId = ref<string | null>(null)

const form = ref({ icon: '', key: '', value: '' })

onMounted(() => {
  fetchContacts()
})

function fetchContacts() {
  loading.value = true
  store.fetchContacts().then(() => { loading.value = false })
}

function openAdd() {
  editingId.value = null
  form.value = { icon: '', key: '', value: '' }
  showForm.value = true
}

function openEdit(contact: ContactEntry) {
  editingId.value = contact.id
  form.value = { icon: contact.icon, key: contact.key, value: contact.value }
  showForm.value = true
}

function save() {
  saving.value = true
  const promise = editingId.value
    ? store.updateContact({ id: editingId.value, ...form.value })
    : store.createContact(form.value)

  promise
    .then(() => {
      saving.value = false
      showForm.value = false
      notify.success(editingId.value ? 'Contact updated' : 'Contact created')
      fetchContacts()
    })
    .catch(() => {
      saving.value = false
      notify.error('Failed to save contact')
    })
}
</script>

<template>
  <div class="flex flex-col gap-4">

    <!-- Header -->
    <div class="flex items-center justify-between">
      <p class="text-sm font-medium text-gray-700"></p>
      <button @click="openAdd" class="btn-primary text-sm">
        <i class="fa fa-plus mr-2"></i>Add Contact
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="py-10 text-center text-gray-400">
      <i class="fa-solid fa-spinner fa-spin text-2xl mb-2"></i>
      <p class="text-sm">Loading...</p>
    </div>

    <!-- Table -->
    <div v-else class="bg-white shadow overflow-hidden">
      <table class="w-full text-xs">
        <thead class="bg-gray-50 sticky top-0">
          <tr>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">#</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Icon</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Key</th>
            <th class="text-left py-3 px-4 font-semibold text-gray-700 border-b border-gray-200">Value</th>
            <th class="text-center py-3 px-4 font-semibold text-gray-700 border-b border-gray-200"></th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(contact, idx) in store.contacts"
            :key="contact.id"
            class="hover:bg-gray-50 transition border-b border-gray-100">
            <td class="py-2 px-4 text-gray-500">{{ idx + 1 }}</td>
            <td class="py-2 px-4">
              <i :class="contact.icon" class="text-primary text-base"></i>
            </td>
            <td class="py-2 px-4 font-semibold text-gray-800">{{ contact.key }}</td>
            <td class="py-2 px-4 text-gray-600">{{ contact.value }}</td>
            <td class="py-2 px-4 text-center">
              <button @click="openEdit(contact)" class="btn-sm-primary">
                <i class="fa fa-pencil mr-1"></i>Edit
              </button>
            </td>
          </tr>
          <tr v-if="!store.contacts || store.contacts.length === 0">
            <td colspan="5" class="py-12 text-center">
              <div class="flex flex-col items-center text-gray-400">
                <i class="fa-solid fa-address-book text-4xl mb-3 text-gray-300"></i>
                <p class="text-sm">No contact entries configured yet</p>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

  </div>

  <!-- Form Modal -->
  <AppModal v-model="showForm" sm>
    <h3 class="text-base font-semibold text-gray-900 mb-4">
      {{ editingId ? 'Edit Contact' : 'Add Contact' }}
    </h3>
    <div class="space-y-4">
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Icon <span class="text-red-500">*</span>
        </label>
        <input
          v-model="form.icon"
          type="text"
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="e.g. fa-solid fa-phone"
        />
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Key <span class="text-red-500">*</span>
        </label>
        <input
          v-model="form.key"
          type="text"
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="e.g. Phone"
        />
      </div>
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">
          Value <span class="text-red-500">*</span>
        </label>
        <input
          v-model="form.value"
          type="text"
          class="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="e.g. +256 700 000 000"
        />
      </div>
      <div class="flex justify-end gap-2 pt-2 border-t border-gray-100">
        <button
          @click="showForm = false"
          class="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-100">
          Cancel
        </button>
        <button
          @click="save"
          :disabled="saving || !form.icon || !form.key || !form.value"
          class="btn-primary disabled:opacity-50">
          <span v-if="saving">Saving...</span>
          <span v-else>{{ editingId ? 'Update' : 'Save' }}</span>
        </button>
      </div>
    </div>
  </AppModal>
</template>

<style scoped></style>
